// Gemstone data with specific gravity
export const gemstoneData = {
  sapphire: { name: "Sapphire", sg: 4.0, pricePerCarat: { low: 400, mid: 1500, high: 5000 } },
  emerald: { name: "Emerald", sg: 2.72, pricePerCarat: { low: 500, mid: 2000, high: 8000 } },
  ruby: { name: "Ruby", sg: 4.0, pricePerCarat: { low: 600, mid: 2500, high: 10000 } },
  amethyst: { name: "Amethyst", sg: 2.65, pricePerCarat: { low: 10, mid: 30, high: 80 } },
  opal: { name: "Opal", sg: 2.15, pricePerCarat: { low: 50, mid: 200, high: 800 } },
  aquamarine: { name: "Aquamarine", sg: 2.69, pricePerCarat: { low: 80, mid: 300, high: 1000 } },
  topaz: { name: "Topaz", sg: 3.53, pricePerCarat: { low: 30, mid: 150, high: 500 } },
  garnet: { name: "Garnet", sg: 3.65, pricePerCarat: { low: 20, mid: 80, high: 300 } },
  peridot: { name: "Peridot", sg: 3.34, pricePerCarat: { low: 50, mid: 150, high: 450 } },
  tanzanite: { name: "Tanzanite", sg: 3.35, pricePerCarat: { low: 200, mid: 800, high: 2500 } },
}

export type GemstoneType = keyof typeof gemstoneData
export type ShapeType = "round" | "oval" | "emerald" | "pear" | "cushion" | "princess" | "marquise" | "heart"
export type QualityGrade = "low" | "mid" | "high"

export const shapeMeta: Record<ShapeType, { name: string; factor: number }> = {
  round: { name: "Round", factor: 0.0018 },
  oval: { name: "Oval", factor: 0.002 },
  emerald: { name: "Emerald Cut", factor: 0.0025 },
  pear: { name: "Pear", factor: 0.0018 },
  cushion: { name: "Cushion", factor: 0.0022 },
  princess: { name: "Princess", factor: 0.0026 },
  marquise: { name: "Marquise", factor: 0.0016 },
  heart: { name: "Heart", factor: 0.0021 },
}

export const qualityGrades: Record<QualityGrade, { label: string; hint: string }> = {
  low: {
    label: "Commercial",
    hint: "Visible inclusions, lighter color, trade-grade material",
  },
  mid: {
    label: "Fine",
    hint: "Good color, typical jewelry-grade clarity and cut",
  },
  high: {
    label: "Extra Fine",
    hint: "Strong color, high clarity, collector or set-stone quality",
  },
}

export interface FormulaBreakdown {
  length: number
  width: number
  depth: number
  volume: number
  shapeFactor: number
  shapeName: string
  specificGravity: number
  gemName: string
  pricePerCarat: number
  quality: QualityGrade
  qualityLabel: string
}

export interface CalculationResult {
  caratWeight: number
  estimatedValue: number
  valueRange: { low: number; mid: number; high: number }
  formula: FormulaBreakdown
}

export function calculateCaratWeight(
  shape: ShapeType,
  length: number,
  width: number,
  depth: number,
  gemstoneType: GemstoneType
): number {
  const gem = gemstoneData[gemstoneType]
  const shapeFactor = shapeMeta[shape].factor
  const volume = length * width * depth
  const caratWeight = volume * shapeFactor * gem.sg
  return Math.round(caratWeight * 100) / 100
}

export function calculateValue(
  caratWeight: number,
  gemstoneType: GemstoneType,
  quality: QualityGrade
): number {
  const gem = gemstoneData[gemstoneType]
  return Math.round(caratWeight * gem.pricePerCarat[quality])
}

export function getFullCalculation(
  shape: ShapeType,
  length: number,
  width: number,
  depth: number,
  gemstoneType: GemstoneType,
  quality: QualityGrade = "mid"
): CalculationResult {
  const gem = gemstoneData[gemstoneType]
  const shapeInfo = shapeMeta[shape]
  const volume = Math.round(length * width * depth * 100) / 100
  const caratWeight = calculateCaratWeight(shape, length, width, depth, gemstoneType)
  const estimatedValue = calculateValue(caratWeight, gemstoneType, quality)

  return {
    caratWeight,
    estimatedValue,
    valueRange: {
      low: calculateValue(caratWeight, gemstoneType, "low"),
      mid: calculateValue(caratWeight, gemstoneType, "mid"),
      high: calculateValue(caratWeight, gemstoneType, "high"),
    },
    formula: {
      length,
      width,
      depth,
      volume,
      shapeFactor: shapeInfo.factor,
      shapeName: shapeInfo.name,
      specificGravity: gem.sg,
      gemName: gem.name,
      pricePerCarat: gem.pricePerCarat[quality],
      quality,
      qualityLabel: qualityGrades[quality].label,
    },
  }
}

// Birthstone mapping
export const birthstones: Record<number, { stone: string, vedic: string }> = {
  1: { stone: "Garnet", vedic: "Garnet or Ruby" },
  2: { stone: "Amethyst", vedic: "Amethyst or Pearl" },
  3: { stone: "Aquamarine", vedic: "Aquamarine or Red Coral" },
  4: { stone: "Diamond", vedic: "Diamond or Opal" },
  5: { stone: "Emerald", vedic: "Emerald" },
  6: { stone: "Pearl", vedic: "Pearl or Moonstone" },
  7: { stone: "Ruby", vedic: "Ruby or Red Spinel" },
  8: { stone: "Peridot", vedic: "Peridot or Sardonyx" },
  9: { stone: "Sapphire", vedic: "Blue Sapphire" },
  10: { stone: "Opal", vedic: "Opal or Tourmaline" },
  11: { stone: "Topaz", vedic: "Yellow Topaz or Citrine" },
  12: { stone: "Tanzanite", vedic: "Turquoise or Blue Topaz" },
}
